import { CSSProperties, ReactNode } from 'react';

import { FlexAligner, Tooltip, Typography } from '@scaleaq/scaleui';
import styled from 'styled-components';

import { Z_INDEX } from '@/constants';
import { Icon } from '@/utils';
import { colors, responsive } from '@/utils';

import { InfoOverLay } from './InfoOverLay.tsx';
import { StyledSummaryCard } from './summaryCard.style.ts';

/**
 * Summary card with optional icon, click handler and optional config (disabled, tooltip, layout).
 * Backward compatible with the original lib API: pass only Icon, title, onClick for the simple case.
 * Pass optional bodyStyle, disabled, summaryInfo, titleMinHeight, style for the extended config case.
 */
type SummaryCardProps = {
  Icon: ReactNode;
  bodyStyle?: CSSProperties;
  disabled?: boolean;
  onClick: () => void;
  style?: CSSProperties;
  summaryInfo?: string;
  title: string;
  titleMinHeight?: string;
};
export const SummaryCard = ({
  Icon: SummaryIcon,
  bodyStyle,
  disabled = false,
  onClick,
  style,
  title,
  titleMinHeight,
  summaryInfo = '',
}: SummaryCardProps) => {
  const hasSummaryInfo = Boolean(summaryInfo?.trim());

  return (
    <StyledSummaryCard
      padding={`${responsive.spacing['5xlarge']} ${responsive.spacing['4xlarge']} ${responsive.spacing['4xlarge']}`}
      style={{ ...style, cursor: disabled ? 'default' : 'pointer' }}
      onClick={disabled ? undefined : () => onClick()}
      tabIndex={disabled ? -1 : 0}
      role='button'
      aria-disabled={disabled}
      className={disabled ? 'is-disabled' : undefined}
      onKeyDown={
        disabled
          ? undefined
          : e => {
              if (e.key === 'Enter' || e.key === ' ') {
                onClick();
              }
            }
      }
    >
      {disabled && <InfoOverLay />}
      {hasSummaryInfo && (
        <SummaryCardInfoIconWrapper
          style={disabled ? { pointerEvents: 'auto' } : undefined}
          onClick={e => e.stopPropagation()}
        >
          <Tooltip
            tooltip={<Typography type={'body_large'}>{summaryInfo}</Typography>}
            placement='top'
          >
            <Icon.Info
              size='large'
              style={{
                color: disabled ? colors.icon.warning[100] : colors.icon.info[100],
                minWidth: '1.5rem',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </SummaryCardInfoIconWrapper>
      )}
      <FlexAligner direction='column' spaceBetween fullHeight style={bodyStyle}>
        <Typography
          className='title'
          {...(titleMinHeight != null && { style: { minHeight: titleMinHeight } })}
          type='heading_large'
          color={`${colors.text.info.on.soft[100]}`}
        >
          {title}
        </Typography>
        {SummaryIcon}
      </FlexAligner>
      <Arrow>
        <Icon.ArrowRight size='large' className='arrow-forward-icon' />
        <Icon.ArrowRight size='large' className='push-arrow' />
      </Arrow>
    </StyledSummaryCard>
  );
};

const SummaryCardInfoIconWrapper = styled.div`
  position: absolute;
  top: ${responsive.spacing['large']};
  right: ${responsive.spacing['large']};
  z-index: ${Z_INDEX.overlay.panel};
  padding: ${responsive.spacing.small};
`;

const Arrow = styled(FlexAligner)`
  position: absolute;
  left: calc(100% - 2.5rem);
  bottom: 1rem;
`;
